class SnapSlider {
    /**
     * @param {Element} root
     */
    constructor(root) {
        this.root = /** @type {HTMLElement} */ (root);
        this.box = /** @type {HTMLElement} */ (this.root.querySelector('.slider-box'));
        this.items = /** @type {HTMLElement[]} */ ([...this.box.querySelectorAll('.slider-item, .group-block')]);

        if (!this.items.length) return;

        this.leftBtn = /** @type {HTMLElement | null} */ (this.root.querySelector('.slider-left'));
        this.rightBtn = /** @type {HTMLElement | null} */ (this.root.querySelector('.slider-right'));
        this.bottom = /** @type {HTMLElement | null} */ (this.root.querySelector('.slider-bottom'));
        
        // 【修改点1】获取 dots 的容器，而不是具体的 item
        this.dotsContainer = /** @type {HTMLElement | null} */ (this.root.querySelector('.slider-dolts'));
        // 初始化 dots 数组，稍后动态填充
        this.dots = [];

        this.index = 0;
        this.itemWidth = 0;
        this.maxIndex = 0;

        this.handleResize = this.handleResize.bind(this);

        // 初始化计算 (calc 内部会调用 createDots)
        this.calc(); 
        this.bind();
        
        // 初始状态更新UI (激活正确的点和按钮状态)
        this.updateUI();
    }

    calc() {
        const item = this.items[0];
        if (!item) return;

        const style = getComputedStyle(this.box);
        const gap = parseFloat(style.gap) || 0;

        this.itemWidth = item.offsetWidth + gap || 1; 

        // scrollWidth 是内容总宽度，clientWidth 是可视区域宽度
        // 这里使用 Math.ceil 向上取整，确保在某些亚像素情况下滚动到底部
        const maxScroll = Math.ceil(this.box.scrollWidth - this.box.clientWidth);
        
        // 计算最大索引值。
        this.maxIndex = Math.max(0, Math.round(maxScroll / this.itemWidth));

        // 没有可滑动内容时隐藏底部导航点
        this.bottom?.classList.toggle('hidden', this.maxIndex <= 0);

        // 【修改点2】核心逻辑：根据 maxIndex 动态创建所需数量的点
        this.createDots();

        // 重新计算后，如果当前索引超出了新的最大索引，需要修正
        const maxIndex = /** @type {number} */ (this.maxIndex);
        const index = /** @type {number} */ (this.index);
        if (index > maxIndex) {
            this.index = maxIndex;
        }
    }

    // 【新增方法】动态创建导航点
    createDots() {
        if (!this.dotsContainer) return;

        // 1. 清空现有的点
        this.dotsContainer.innerHTML = '';

        // 2. 需要的点数 = 最大索引 + 1
        const dotsCount = /** @type {number} */ (this.maxIndex) + 1;

        // 3. 循环创建新的 DOM 元素
        const newDots = [];
        for (let i = 0; i < dotsCount; i++) {
            const dot = document.createElement('div');
            // 添加原始 HTML 中的类名
            dot.classList.add('slider-dolts-item');
            // 可选：添加点击事件支持
            dot.addEventListener('click', () => this.go(i));
            this.dotsContainer.appendChild(dot);
            newDots.push(dot);
        }

        // 4. 更新 this.dots 引用，供 updateUI 使用
        this.dots = newDots;
    }

    bind() {
        this.leftBtn?.addEventListener('click', () => this.go(/** @type {number} */ (this.index) - 1));
        this.rightBtn?.addEventListener('click', () => this.go(/** @type {number} */ (this.index) + 1));

        let ticking = false;
        this.box.addEventListener('scroll', () => {
            if (ticking) return;
            ticking = true;

            requestAnimationFrame(() => {
                if (/** @type {number} */ (this.itemWidth) > 0) {
                    const newIndex = Math.round(this.box.scrollLeft / /** @type {number} */ (this.itemWidth));
                    // 只有索引真正改变时才更新UI，提升性能
                    if (newIndex !== this.index) {
                         this.index = newIndex;
                         this.updateUI();
                    }
                }
                ticking = false;
            });
        });

        if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(this.handleResize);
            this.resizeObserver.observe(this.box);
        } else {
            (/** @type {Window} */ (window)).addEventListener('resize', /** @type {EventListener} */ (this.handleResize));
        }
    }
    
    handleResize() {
        // 尺寸改变，重新计算 (会触发 createDots 重建点)
        this.calc();
        // 重新定位到当前索引并更新UI
        this.go(/** @type {number} */ (this.index), true); 
    }

    /**
     * @param {number} i
     * @param {boolean} [instant=false]
     */
    go(i, instant = false) {
        const targetIndex = Math.max(0, Math.min(i, /** @type {number} */ (this.maxIndex)));
        
        if (targetIndex === this.index && !instant) return;
        
        this.index = targetIndex;

        this.box.scrollTo({
            left: /** @type {number} */ (this.index) * /** @type {number} */ (this.itemWidth),
            behavior: instant ? 'auto' : 'smooth'
        });
        
        this.updateUI();
    }

    updateUI() {
        this.leftBtn?.classList.toggle('active', /** @type {number} */ (this.index) > 0);
        this.rightBtn?.classList.toggle('active', /** @type {number} */ (this.index) < /** @type {number} */ (this.maxIndex));

        // 安全检查：确保 dots 数组存在
        const dots = /** @type {HTMLElement[]} */ (this.dots);
        if (!dots.length) return;

        // 激活当前索引对应的点
        // 由于我们动态创建了正确数量的点，这里不需要再做复杂的越界处理，直接匹配索引即可
        dots.forEach((dot, i) => {
            dot.classList.toggle('active', i === /** @type {number} */ (this.index));
        });
    }
}

// 初始化函数
function initSliders() {
    document.querySelectorAll('.slider').forEach((slider) => {
        if (!(slider instanceof HTMLElement)) return;
        /** @type {HTMLElement & {_snapInited?: boolean}} */
        const sliderElement = slider;
        if (!sliderElement._snapInited) {
            sliderElement._snapInited = true;
            new SnapSlider(slider);
        }
    });
}

// ===== 新增：监听动态添加的 .slider =====
function observeDynamicSliders() {
    const observer = new MutationObserver((mutations) => {
        let hasNewSlider = false;
        for (const mutation of mutations) {
            if (mutation.type === 'childList') {
                for (const node of mutation.addedNodes) {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        // 检查新增节点自身是否是 .slider，或其后代包含 .slider
                        if (node.matches && node.matches('.slider')) {
                            hasNewSlider = true;
                            break;
                        }
                        if (node.querySelector && node.querySelector('.slider')) {
                            hasNewSlider = true;
                            break;
                        }
                    }
                }
            }
            if (hasNewSlider) break;
        }
        if (hasNewSlider) {
            initSliders(); // 只会初始化未被标记的 .slider
        }
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true,
    });
}

// ===== 注册初始化和监听 =====
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        initSliders();
        observeDynamicSliders();
    });
} else {
    initSliders();
    observeDynamicSliders();
}

// 保留 load 事件，确保图片等资源加载完成后重新计算尺寸
window.addEventListener('load', initSliders);



// product card mouse hover
document.addEventListener('mousemove', (e) => {
    const card = e.target.closest('.card-gallery');
    if (!card) return;

    const mouse = card.querySelector('.mouse');
    if (!mouse) return;

    const rect = card.getBoundingClientRect();

    mouse.style.transform = `translate3d(${e.clientX - rect.left}px, ${e.clientY - rect.top}px, 0) translate(-50%, -50%)`;
});

document.addEventListener('mouseover', (e) => {
    const card = e.target.closest('.card-gallery');
    if (!card) return;

    const mouse = card.querySelector('.mouse');
    if (!mouse) return;

    mouse.style.opacity = '1';
    mouse.style.visibility = 'visible';
});

document.addEventListener('mouseout', (e) => {
    const card = e.target.closest('.card-gallery');
    if (!card) return;

    // 只有真正离开整个 card 时才隐藏
    if (card.contains(e.relatedTarget)) return;

    const mouse = card.querySelector('.mouse');
    if (!mouse) return;

    mouse.style.opacity = '0';
    mouse.style.visibility = 'hidden';
});


// You may also like
document.addEventListener('DOMContentLoaded', function() {
  const container = document.querySelector('#you-may-also-like');
  if (!container) return;

  const url = container.dataset.url;
  if (!url) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        fetch(url)
          .then(response => response.text())
          .then(html => {
            // 替换父级整个内容
            container.parentElement.innerHTML = html;
          })
          .catch(e => console.error('Recommendations load failed', e));
        observer.unobserve(container);
      }
    });
  }, { rootMargin: '0px 0px 200px 0px' });

  observer.observe(container);
});

document.addEventListener('DOMContentLoaded', function() {
    (function() {
        const target = document.querySelector('.product-details');
        if (!target) {
            console.warn('未找到 .product-details 元素');
            return;
        }

        // 获取真正滚动容器的函数（保持不变）
        function getScrollContainer() {
            if (document.scrollingElement && document.scrollingElement.scrollHeight > window.innerHeight) {
                return document.scrollingElement;
            }
            const all = document.querySelectorAll('*');
            for (let el of all) {
                const style = window.getComputedStyle(el);
                if ((style.overflow === 'auto' || style.overflow === 'scroll' || 
                    style.overflowY === 'auto' || style.overflowY === 'scroll') &&
                    el.scrollHeight > el.clientHeight) {
                    return el;
                }
            }
            return window;
        }

        const scrollContainer = getScrollContainer();
        // console.log('滚动容器是:', scrollContainer);

        // 计算最大允许滚动距离（元素高度 - 窗口高度）
        function getMaxScroll() {
            const elementHeight = target.offsetHeight; // 或 scrollHeight，根据需求
            const windowHeight = window.innerHeight;
            // 如果元素高度小于窗口高度，则最大滚动距离为 0（即不进行上移）
            return Math.max(0, elementHeight - windowHeight);
        }

        function updateStickyTop() {
            const scrollY = (scrollContainer === window) ? window.scrollY : scrollContainer.scrollTop;
            // console.log('滚动距离 scrollY =', scrollY);

            const maxScroll = getMaxScroll();
            // 限制 scrollY 不超过 maxScroll
            const limitedScroll = Math.min(scrollY, maxScroll);
            // top 设置为负的 limitedScroll
            target.style.top = '-' + limitedScroll + 'px';
            // console.log('设置 top = -' + limitedScroll + 'px');
        }

        // 绑定滚动事件
        scrollContainer.addEventListener('scroll', updateStickyTop, { passive: true });
        // 窗口大小变化时重新计算（因为高度可能改变）
        window.addEventListener('resize', updateStickyTop);
        // 页面完全加载后执行一次
        window.addEventListener('load', function() {
            requestAnimationFrame(updateStickyTop);
        });
        // 立即执行一次
        updateStickyTop();
    })();
});
